<template>
  <div class="session-calendar">
    <!-- 自建月曆：取代 v-date-picker，以支援每格自訂 className -->
    <div class="sc-picker">
      <!-- Header -->
      <div class="sc-header">
        <button
          class="sc-header__nav"
          :disabled="isPrevMonthDisabled"
          :aria-label="$t('previous_month') || 'Previous month'"
          @click="prevMonth"
        >
          <v-icon>mdi-menu-left</v-icon>
        </button>
        <span class="sc-header__title">{{ headerTitle }}</span>
        <button
          class="sc-header__nav"
          :disabled="isNextMonthDisabled"
          :aria-label="$t('next_month') || 'Next month'"
          @click="nextMonth"
        >
          <v-icon>mdi-menu-right</v-icon>
        </button>
      </div>

      <!-- Weekday labels -->
      <div class="sc-weekdays" role="row">
        <div
          v-for="label in weekdayLabels"
          :key="label"
          class="sc-weekday"
          role="columnheader"
        >
          {{ label }}
        </div>
      </div>

      <!-- Calendar grid -->
      <div class="sc-body" role="grid" @mouseleave="hoveredDate = ''">
        <div
          v-for="(week, wi) in calendarGrid"
          :key="wi"
          class="sc-week"
          role="row"
        >
          <div
            v-for="(cell, di) in week"
            :key="di"
            class="sc-cell"
            role="gridcell"
          >
            <button
              v-if="cell"
              :disabled="!cell.isAllowed"
              :class="[
                'sc-day',
                cell.isAllowed && !cell.isMultiDay ? 'sc-day--single' : '',
                cell.isAllowed && cell.isMultiDay ? 'sc-day--multi' : '',
                cell.isAllowed && cell.isMultiDayStart
                  ? 'sc-day--starting'
                  : '',
                cell.isAllowed && cell.isMultiDayEnd ? 'sc-day--ending' : '',
                selectedSessionDates.has(cell.date) ? 'sc-day--active' : '',
                hoveredSessionDates.has(cell.date) &&
                !selectedSessionDates.has(cell.date)
                  ? 'sc-day--hover'
                  : '',
              ]"
              :aria-label="cell.date"
              :aria-pressed="selectedDate === cell.date"
              :aria-disabled="!cell.isAllowed"
              @mouseenter="cell.isAllowed && (hoveredDate = cell.date)"
              @click="cell.isAllowed && onDateClick(cell.date)"
            >
              <span class="sc-day__number">{{ cell.day }}</span>
              <span
                v-if="cell.isAllowed"
                class="sc-day__dot"
                aria-hidden="true"
              ></span>
            </button>
            <div
              v-else
              class="sc-day sc-day--placeholder"
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="hasClickedDate">
      <div
        class="session-calendar__sessions mt-4"
        v-if="sessionsOnSelectedDate.length"
      >
        <SessionListItem
          :sessions="sessionsOnSelectedDate"
          :eventId="eventId"
          @emitGetSessionStatus="$emit('emitGetSessionStatus')"
        />
      </div>

      <div
        class="session-calendar__empty text-center pa-4 grey--text"
        v-else-if="selectedDate && !sessionsOnSelectedDate.length"
      >
        {{ $t("activity_calendar_no_event") }}
      </div>
    </template>

    <div
      class="session-calendar__empty text-center pt-11 pb-14"
      v-else-if="!hasSessionsInCurrentMonth"
    >
      {{ $t("activity_calendar_no_event") }}
    </div>

    <div class="session-calendar__hint text-center pt-11 pb-14" v-else>
      {{ $t("activity_calendar_select_date_hint") }}
    </div>
  </div>
</template>

<script>
import SessionListItem from "@/views/components/SessionListItem";
import dayjs from "dayjs";

export default {
  name: "SessionCalendar",
  components: { SessionListItem },
  props: {
    sessions: {
      type: Array,
      default: () => [],
    },
    eventId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      selectedDate: "",
      pickerMonth: dayjs().format("YYYY-MM"),
      hasClickedDate: false,
      hoveredDate: "",
    };
  },
  computed: {
    currentLocale() {
      const map = { zh: "zh-TW", en: "en", jp: "ja", kr: "ko" };
      return map[this.$store.getters.locale] || "zh-TW";
    },
    // 以 hoveredDate 所屬場次為單位，回傳整組日期集合
    hoveredSessionDates() {
      if (!this.hoveredDate) return new Set();
      return this.getSessionDatesSet(this.hoveredDate);
    },
    // 以 selectedDate 所屬場次為單位，回傳整組日期集合
    selectedSessionDates() {
      if (!this.selectedDate) return new Set();
      return this.getSessionDatesSet(this.selectedDate);
    },
    // 跨日場次的所有日期集合（含開始、中間、結束日）
    multiDayDates() {
      const dates = new Set();
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        if (dateParts.length < 2) return;
        const start = dayjs(dateParts[0].trim());
        const end = dayjs(dateParts[dateParts.length - 1].trim());
        if (
          !start.isValid() ||
          !end.isValid() ||
          end.isBefore(start) ||
          end.isSame(start, "day")
        )
          return;
        let current = start;
        while (!current.isAfter(end)) {
          dates.add(current.format("YYYY-MM-DD"));
          current = current.add(1, "day");
        }
      });
      return dates;
    },
    // 跨日場次的結束日集合
    multiDayEndDates() {
      const dates = new Set();
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        if (dateParts.length < 2) return;
        const start = dayjs(dateParts[0].trim());
        const end = dayjs(dateParts[dateParts.length - 1].trim());
        if (!start.isValid() || !end.isValid() || end.isSame(start, "day"))
          return;
        dates.add(end.format("YYYY-MM-DD"));
      });
      return dates;
    },
    // 跨日場次的開始日集合
    multiDayStartDates() {
      const dates = new Set();
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        if (dateParts.length < 2) return;
        const start = dayjs(dateParts[0].trim());
        const end = dayjs(dateParts[dateParts.length - 1].trim());
        if (!start.isValid() || !end.isValid() || end.isSame(start, "day"))
          return;
        dates.add(start.format("YYYY-MM-DD"));
      });
      return dates;
    },
    sessionDates() {
      const seen = new Set();
      const dates = [];
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        if (dateParts.length >= 2) {
          const start = dayjs(dateParts[0].trim());
          const end = dayjs(dateParts[dateParts.length - 1].trim());
          // 開始與結束同一天時，視為單日場次
          if (
            !start.isValid() ||
            !end.isValid() ||
            end.isBefore(start) ||
            end.isSame(start, "day")
          ) {
            const formatted = start.format("YYYY-MM-DD");
            if (formatted !== "Invalid Date" && !seen.has(formatted)) {
              seen.add(formatted);
              dates.push(formatted);
            }
            return;
          }
          let current = start;
          while (!current.isAfter(end)) {
            const formatted = current.format("YYYY-MM-DD");
            if (!seen.has(formatted)) {
              seen.add(formatted);
              dates.push(formatted);
            }
            current = current.add(1, "day");
          }
        } else {
          const formatted = dayjs(dateParts[0].trim()).format("YYYY-MM-DD");
          if (formatted !== "Invalid Date" && !seen.has(formatted)) {
            seen.add(formatted);
            dates.push(formatted);
          }
        }
      });
      return dates;
    },
    // 自建月曆格資料，每格帶有 isAllowed / isMultiDay className 依據
    calendarGrid() {
      const firstOfMonth = dayjs(this.pickerMonth + "-01");
      const daysInMonth = firstOfMonth.daysInMonth();
      const startDow = firstOfMonth.day(); // 0=Sunday
      const sessionSet = new Set(this.sessionDates);

      const cells = [];
      for (let i = 0; i < startDow; i++) cells.push(null);
      for (let d = 1; d <= daysInMonth; d++) {
        const date = firstOfMonth.add(d - 1, "day").format("YYYY-MM-DD");
        cells.push({
          date,
          day: d,
          isAllowed: sessionSet.has(date),
          isMultiDay: this.multiDayDates.has(date),
          isMultiDayStart: this.multiDayStartDates.has(date),
          isMultiDayEnd: this.multiDayEndDates.has(date),
        });
      }
      while (cells.length % 7 !== 0) cells.push(null);

      const weeks = [];
      for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
      }
      return weeks;
    },
    weekdayLabels() {
      const labels = {
        "zh-TW": ["日", "一", "二", "三", "四", "五", "六"],
        en: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
        ja: ["日", "月", "火", "水", "木", "金", "土"],
        ko: ["일", "월", "화", "수", "목", "금", "토"],
      };
      return labels[this.currentLocale] || labels["zh-TW"];
    },
    headerTitle() {
      return this.headerDateFormat(this.pickerMonth);
    },
    minMonth() {
      const today = dayjs().startOf("day");
      const futureMonths = new Set();
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        // 如果是跨日場次，使用結束時間（最後一個日期）作為判斷依據
        const dateToCheck = dateParts[dateParts.length - 1];
        const parsed = dayjs(dateToCheck.trim());
        if (!parsed.isValid()) return;
        if (parsed.startOf("day").diff(today, "day") >= 0) {
          futureMonths.add(parsed.format("YYYY-MM"));
        }
      });
      if (futureMonths.size === 0) return dayjs().format("YYYY-MM");
      return [...futureMonths].sort()[0];
    },
    isPrevMonthDisabled() {
      return this.pickerMonth <= this.minMonth;
    },
    maxMonth() {
      const today = dayjs().startOf("day");
      let maxDate = null;
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        // 如果是跨日場次，使用結束時間（最後一個日期）作為判斷依據
        const dateToCheck = dateParts[dateParts.length - 1];
        const parsed = dayjs(dateToCheck.trim());
        if (!parsed.isValid()) return;
        if (parsed.startOf("day").diff(today, "day") >= 0) {
          if (!maxDate || parsed.isAfter(maxDate)) {
            maxDate = parsed;
          }
        }
      });
      return maxDate ? maxDate.format("YYYY-MM") : dayjs().format("YYYY-MM");
    },
    isNextMonthDisabled() {
      return this.pickerMonth >= this.maxMonth;
    },
    hasSessionsInCurrentMonth() {
      return this.sessionDates.some((date) =>
        date.startsWith(this.pickerMonth)
      );
    },
    nearestSession() {
      const today = dayjs().startOf("day");
      let nearest = null;
      let minDiff = Infinity;

      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        // 檢查跨日場次是否有效：使用結束時間（最後一個日期）作為判斷依據
        const endDate = dateParts[dateParts.length - 1];
        const endDateParsed = dayjs(endDate.trim());
        if (!endDateParsed.isValid()) return;

        // 如果結束時間已過期，則跳過此場次
        if (endDateParsed.startOf("day").diff(today, "day") < 0) return;

        // 使用開始時間（或單日時間）來計算最近距離
        const startDate = dateParts[0];
        const sessionDay = dayjs(startDate.trim());
        if (!sessionDay.isValid()) return;

        const diff = sessionDay.startOf("day").diff(today, "day");
        // 多日場次：若開始日已過但結束日尚未到（進行中），視 effectiveDiff 為 0
        const isMultiDay =
          dateParts.length >= 2 &&
          !endDateParsed.isSame(sessionDay, "day");
        const effectiveDiff = isMultiDay && diff < 0 ? 0 : diff;
        if (effectiveDiff >= 0 && effectiveDiff < minDiff) {
          minDiff = effectiveDiff;
          nearest = session;
        }
      });
      return nearest;
    },
    sessionsOnSelectedDate() {
      if (!this.selectedDate) return [];
      const selected = dayjs(this.selectedDate);
      return this.sessions.filter((session) => {
        if (!session.date) return false;
        const dateParts = this.parseDateParts(session.date);
        if (dateParts.length >= 2) {
          const start = dayjs(dateParts[0].trim());
          const end = dayjs(dateParts[dateParts.length - 1].trim());
          if (!start.isValid() || !end.isValid()) return false;
          // 開始與結束同一天，視為單日場次，直接比對開始日期
          if (end.isSame(start, "day")) {
            return start.format("YYYY-MM-DD") === this.selectedDate;
          }
          // 跨日場次：判斷選取日期是否落在起始與結束日期範圍內（含兩端）
          return (
            (selected.isSame(start, "day") || selected.isAfter(start, "day")) &&
            (selected.isSame(end, "day") || selected.isBefore(end, "day"))
          );
        }
        return (
          dayjs(dateParts[0].trim()).format("YYYY-MM-DD") === this.selectedDate
        );
      });
    },
  },
  watch: {
    pickerMonth() {
      this.selectedDate = "";
      this.hasClickedDate = false;
    },
  },
  mounted() {
    if (this.nearestSession && this.nearestSession.date) {
      const parts = this.parseDateParts(this.nearestSession.date);
      const nearestDay = parts.reduce((closest, d) => {
        const parsed = dayjs(d.trim());
        if (!parsed.isValid()) return closest;
        const today = dayjs().startOf("day");
        const diff = parsed.startOf("day").diff(today, "day");
        if (diff >= 0 && (!closest || diff < closest.diff)) {
          return { date: parsed, diff };
        }
        return closest;
      }, null);
      if (nearestDay) {
        this.pickerMonth = nearestDay.date.format("YYYY-MM");
      }
    }
  },
  methods: {
    parseDateParts(dateString) {
      return dateString.indexOf(" ~ ") > -1
        ? dateString.split(" ~ ")
        : [dateString];
    },
    // 給定一個日期，找出它所屬的所有場次，並回傳這些場次涵蓋的全部日期 Set
    getSessionDatesSet(date) {
      const result = new Set();
      const target = dayjs(date);
      if (!target.isValid()) return result;
      this.sessions.forEach((session) => {
        if (!session.date) return;
        const dateParts = this.parseDateParts(session.date);
        if (dateParts.length >= 2) {
          const start = dayjs(dateParts[0].trim());
          const end = dayjs(dateParts[dateParts.length - 1].trim());
          if (!start.isValid() || !end.isValid()) return;
          const isMultiDay = !end.isSame(start, "day");
          if (isMultiDay) {
            // 判斷 date 是否落在此跨日場次範圍內
            if (
              (target.isSame(start, "day") || target.isAfter(start, "day")) &&
              (target.isSame(end, "day") || target.isBefore(end, "day"))
            ) {
              let current = start;
              while (!current.isAfter(end)) {
                result.add(current.format("YYYY-MM-DD"));
                current = current.add(1, "day");
              }
            }
          } else {
            // 同日視為單日場次
            if (start.format("YYYY-MM-DD") === date) {
              result.add(date);
            }
          }
        } else {
          const d = dayjs(dateParts[0].trim()).format("YYYY-MM-DD");
          if (d === date) result.add(d);
        }
      });
      return result;
    },
    prevMonth() {
      this.pickerMonth = dayjs(this.pickerMonth + "-01")
        .subtract(1, "month")
        .format("YYYY-MM");
    },
    nextMonth() {
      this.pickerMonth = dayjs(this.pickerMonth + "-01")
        .add(1, "month")
        .format("YYYY-MM");
    },
    onDateClick(date) {
      this.selectedDate = date;
      this.hasClickedDate = true;
    },
    headerDateFormat(date) {
      const monthNames = {
        "zh-TW": [
          "一月",
          "二月",
          "三月",
          "四月",
          "五月",
          "六月",
          "七月",
          "八月",
          "九月",
          "十月",
          "十一月",
          "十二月",
        ],
        en: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        ja: [
          "1月",
          "2月",
          "3月",
          "4月",
          "5月",
          "6月",
          "7月",
          "8月",
          "9月",
          "10月",
          "11月",
          "12月",
        ],
        ko: [
          "1월",
          "2월",
          "3월",
          "4월",
          "5월",
          "6월",
          "7월",
          "8월",
          "9월",
          "10월",
          "11월",
          "12월",
        ],
      };
      const [year, month] = date.split("-");
      const locale = this.currentLocale;
      const names = monthNames[locale] || monthNames["zh-TW"];
      return `${names[parseInt(month, 10) - 1]} ${year}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.session-calendar {
  border: 0;

  &__nearest-info {
    background-color: #fff8e1;
    border-left: 4px solid #ffc107;
  }

  &__session-item {
    background-color: #f5f5f5;
    border-left: 3px solid var(--v-primary-base, #1976d2);
    transition: background-color 0.2s;

    &:hover {
      background-color: #eeeeee;
    }
  }
}

// 自建月曆
.sc-picker {
  background-color: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.sc-header {
  background-color: #116993;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;

  &__nav {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.15s;

    &:hover:not(:disabled) {
      background-color: rgba(255, 255, 255, 0.15);
    }

    &:disabled {
      cursor: not-allowed;

      .v-icon {
        color: rgba(255, 255, 255, 0.3) !important;
      }
    }

    .v-icon {
      font-size: 24px;
      color: white;
    }
  }

  &__title {
    color: white;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.01em;
  }
}

.sc-weekdays {
  display: flex;
  background-color: #ebebeb;
}

.sc-weekday {
  flex: 1;
  text-align: center;
  color: #12597abf;
  padding: 16px 0;
  font-size: 14px;
}

.sc-body {
  padding: 12px;

  @media screen and (max-width: 640px) {
    padding: 8px;
  }
}

.sc-week {
  display: flex;
}

.sc-cell {
  flex: 1;
  height: 98px;
  align-content: center;
  position: relative;

  @media screen and (max-width: 640px) {
    height: 48px;
  }
}

.sc-day {
  width: calc(100% - 8px);
  height: 90px;
  border: none;
  border-radius: 6px;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: auto;
  font-size: 16px;
  font-weight: 400;
  color: #12597a40;
  cursor: not-allowed;
  transition: background-color 0.15s;

  @media screen and (max-width: 640px) {
    width: calc(100% - 4px);
    height: 46px;
    border-radius: 0;
    font-size: 12px;
    gap: 4px;
  }

  // 空格占位用
  &--placeholder {
    visibility: hidden;
    pointer-events: none;
  }

  // 單日場次
  &--single,
  &--multi {
    background-color: #c0e6f7;
    color: black;
    font-weight: bold;
    cursor: pointer;

    .sc-day__dot {
      background-color: #12597a;
    }
  }

  // 跨日場次（用溫暗色區隔）
  &--multi {
    &:not(.sc-day--starting):not(.sc-day--ending) {
      border-radius: 0;
    }

    &:not(.sc-day--ending)::after {
      content: "";
      position: absolute;
      display: block;
      width: 8px;
      top: 0;
      bottom: 0;
      margin: auto;
      background-color: #c0e6f7;
      height: 90px;
      right: -4px;
      transition: background-color 0.15s;

      @media screen and (max-width: 640px) {
        width: 6px;
        right: -3px;
        height: 46px;
      }
    }
  }

  // 跨日場次開始日
  &--starting {
    border-radius: 6px 0 0 6px;
  }

  // 跨日場次結束日
  &--ending {
    border-radius: 0 6px 6px 0;
  }

  // 跨日場次同組 hover
  &--hover {
    background-color: darken(#c0e6f7, 8%) !important;

    &.sc-day--multi:not(.sc-day--ending)::after {
      background-color: darken(#c0e6f7, 8%) !important;
    }
  }

  // 被選中的日期
  &--active {
    background-color: #116993 !important;
    color: white !important;
    cursor: pointer;

    &.sc-day--multi:not(.sc-day--ending)::after {
      background-color: #116993 !important;
    }

    .sc-day__dot {
      background-color: white !important;
    }
  }
}

.sc-day__number {
  line-height: 1;
}

.sc-day__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;

  @media screen and (max-width: 640px) {
    width: 6px;
    height: 6px;
  }
}

.session-calendar__empty,
.session-calendar__hint {
  color: #12597a;
}
</style>
